import React, { useCallback, useMemo } from "react";
import { SubmitHandler, useForm, useFormState } from "react-hook-form";
import moment from "moment";

import { discardCopyOf, FormPanel } from "../../../../components/FormPanel";
import { SelectOptionTypes } from "../../../../components/Input";
import {
  buildDraftDetails,
  ConfirmDetail,
  joinDetailParts,
} from "../../../../components/PopUp/confirmShared";
import { useAccountOptions } from "../../../../hooks/useAccountOptions";
import { useNetAmountWhileTaxHidden } from "../../../../hooks/useNetAmountWhileTaxHidden";
import { useSubCategoryResetOnCategoryChange } from "../../../../hooks/useSubCategoryResetOnCategoryChange";
import {
  calculateMaxAllowedPastDate,
  getPastLimitLabel,
} from "../../../../utils/recurringTransactions";
import {
  isTaxed,
  REQUIRED_TAX_WITHHELD_RULES,
} from "../../../../utils/taxWithheld";
import {
  AccountType,
  DateFormat,
  formatCurrency,
  formatDate,
  isAfterLocalToday,
  localToday,
  toIsoDate,
} from "../../../../utils";
import { Category } from "../../types";
import { withTaxForSwitch } from "../../utils/transformers";
import {
  createDefaultTransaction,
  FORM_ID,
  MOBILE_SUBMIT_LABELS,
  SUBMIT_LABELS,
  TONE_BY_TYPE,
  TYPE_OPTIONS,
} from "./constants";
import { DesktopTransactionForm } from "./DesktopTransactionForm";
import { MobileTransactionForm } from "./MobileTransactionForm";
import { useCashbackForm } from "./useCashbackForm";
import {
  CreateTransactionsObject,
  CreateTransactionsProps,
  FREQUENCY_OPTIONS,
  TransactionFieldsProps,
  TransactionTypeOptions,
  Frequency,
} from "./types";

// Maps a transaction type to its discard-preview pill (label + color variant).
const DISCARD_PILL_BY_TYPE: Record<string, { label: string; variant: string }> =
  {
    [TransactionTypeOptions.EXPENSE]: { label: "EXPENSE", variant: "EXPENSE" },
    [TransactionTypeOptions.INCOME]: { label: "INCOME", variant: "INCOME" },
    [TransactionTypeOptions.TRANSFER]: {
      label: "TRANSFER",
      variant: "TRANSFER",
    },
    [TransactionTypeOptions.LOAN_PAYMENT]: {
      label: "LOAN PAYMENT",
      variant: "LOAN",
    },
  };

const useTransactionForm = (
  incomeCategoryList: Category[],
  expenseCategoryList: Category[],
  defaultAccount: SelectOptionTypes | null,
  transaction?: CreateTransactionsObject,
) => {
  // the default transaction is expense, so if we have a defaultAccount, we set it as the fromAccount
  const startValues = transaction ?? {
    ...createDefaultTransaction(),
    fromAccount: defaultAccount ?? null,
  };

  const form = useForm<CreateTransactionsObject>({
    defaultValues: {
      ...startValues,
      isRecurring: transaction?.isRecurring ?? false,
      hasTaxWithheld: isTaxed(transaction?.taxWithheld),
    },
    mode: "onBlur",
    reValidateMode: "onChange",
  });

  const { control, setValue, watch } = form;
  const { isSubmitting } = useFormState({ control });

  const watchedValues = watch([
    "transactionType",
    "category",
    "isRecurring",
    "frequency",
    "interval",
    "hasTaxWithheld",
  ]);
  const [
    transactionType,
    category,
    isRecurring,
    frequency,
    interval,
    hasTaxWithheld,
  ] = watchedValues;

  // Memoized category options based on transaction type
  const categoryOptions = useMemo(() => {
    switch (transactionType.value) {
      case TransactionTypeOptions.INCOME:
        return incomeCategoryList;
      case TransactionTypeOptions.EXPENSE:
        return expenseCategoryList;
      case TransactionTypeOptions.LOAN_PAYMENT:
      default:
        return [];
    }
  }, [transactionType, incomeCategoryList, expenseCategoryList]);

  // Memoized subcategory options
  const subCategoryOptions = useMemo(() => {
    if (transactionType.value === TransactionTypeOptions.TRANSFER) {
      return [];
    }
    const categorySelected: Category | undefined = categoryOptions.find(
      (c) => c.value === category?.value,
    );
    return categorySelected?.subCategories || [];
  }, [category, categoryOptions, transactionType]);

  const defaultAccountIdToSet = defaultAccount ?? null;

  const prevTransactionType = React.useRef(transactionType?.value);

  React.useEffect(() => {
    if (!transactionType) return;

    // Only run if the type actually changed
    if (prevTransactionType.current !== transactionType.value) {
      switch (transactionType.value) {
        case TransactionTypeOptions.LOAN_PAYMENT:
          defaultAccountIdToSet &&
            setValue("fromAccount", defaultAccountIdToSet);
          setValue("toAccount", null);
          setValue("category", null);
          setValue("subCategory", null);
          setValue("principalAmount", "");
          break;
        case TransactionTypeOptions.INCOME:
          defaultAccountIdToSet && setValue("toAccount", defaultAccountIdToSet);
          setValue("fromAccount", null);
          setValue("category", null);
          setValue("subCategory", null);
          break;
        case TransactionTypeOptions.EXPENSE:
          setValue("toAccount", null);
          defaultAccountIdToSet &&
            setValue("fromAccount", defaultAccountIdToSet);
          setValue("category", null);
          setValue("subCategory", null);
          break;
        case TransactionTypeOptions.TRANSFER:
          setValue("category", null);
          setValue("subCategory", null);
          break;
      }

      // Update previous type after handling
      prevTransactionType.current = transactionType.value;
    }
  }, [transactionType, setValue, defaultAccountIdToSet]);

  useSubCategoryResetOnCategoryChange(category?.value, setValue);

  // Recurring can turn on while the switch is hidden (non-Income), which would bring it back on and disabled.
  React.useEffect(() => {
    if (isRecurring) {
      setValue("hasTaxWithheld", false);
    }
  }, [isRecurring, setValue]);

  return {
    form,
    isSubmitting,
    transactionType,
    category,
    categoryOptions,
    subCategoryOptions,
    isRecurring,
    frequency,
    interval,
    hasTaxWithheld,
  };
};

export const TransactionsPanel = ({
  title,
  action,
  isOpen,
  onClose,
  incomeCategoryList,
  expenseCategoryList,
  transaction,
  handleOnSubmit,
  defaultAccount,
}: CreateTransactionsProps) => {
  const { accountOptions, accounts, load } = useAccountOptions({});
  const { accountOptions: liabilityAccountOptions } = useAccountOptions({
    accountType: AccountType.LIABILITIES,
  });

  const originalDate = transaction?.transactionDate
    ? formatDate(transaction.transactionDate, DateFormat.DMY_SPACE)
    : undefined;
  const effectiveTransaction = useMemo(() => {
    if (action === "duplicate" && transaction) {
      const today = toIsoDate(new Date());
      return { ...transaction, transactionDate: today };
    }
    return transaction;
  }, [action, transaction]);

  const {
    form,
    isSubmitting,
    transactionType,
    category,
    categoryOptions,
    subCategoryOptions,
    isRecurring,
    frequency,
    interval,
    hasTaxWithheld,
  } = useTransactionForm(
    incomeCategoryList,
    expenseCategoryList,
    defaultAccount,
    effectiveTransaction,
  );
  const { control, getValues, register, unregister, setValue, clearErrors } =
    form;

  const cashbackFields = useCashbackForm({
    form,
    accounts,
    action,
    original: effectiveTransaction,
  });

  const validationRules = useMemo(
    () => ({
      amount: {
        required: "Amount is required",
        min: { value: 0.01, message: "Amount must be greater than 0" },
        deps: ["taxWithheld"],
      },
      principalAmount: {
        required: "Amount is required",
        min: { value: 0, message: "Amount must be greater than 0" },
        validate: {
          isBelowOrEqual: (value: string) => {
            const amount = Number.parseFloat(getValues("amount"));
            const principalAmount = Number.parseFloat(value);
            if (principalAmount > amount) {
              return "Cannot exceed the Amount";
            }
            return true;
          },
        },
      },
      transactionType: {
        required: "Transaction type is required",
      },
      fromAccountId: {
        required: "From account is required",
        min: { value: 1, message: "From account is required" },
      },
      toAccountId: {
        required: "To account is required",
        min: { value: 1, message: "To account is required" },
      },
      transactionDate: {
        required: "Date is required",
        validate: isRecurring
          ? {
              isWithinLimit: (value: string) => {
                if (!frequency || !interval) return true;

                const selectedDate = moment(value).startOf("day");
                const today = moment().startOf("day");

                // Allow future dates and today
                if (selectedDate.isSameOrAfter(today)) return true;

                // For past dates, check if within allowed limit
                const maxPastDate = calculateMaxAllowedPastDate(
                  frequency.value as Frequency,
                  today.toDate(),
                );
                maxPastDate.setHours(0, 0, 0, 0);

                if (moment(selectedDate).isBefore(maxPastDate)) {
                  const limit = getPastLimitLabel(frequency.value as Frequency);
                  return `For Frequency ${frequency.label}, date cannot be more than ${limit} in the past`;
                }

                return true;
              },
            }
          : {
              isNotFuture: (value: string | Date) =>
                isAfterLocalToday(value)
                  ? "Date cannot be in the future"
                  : true,
            },
      },
      note: {
        minLength: {
          value: 3,
          message: "Description must be at least 3 characters",
        },
        maxLength: {
          value: 255,
          message: "Description cannot exceed 255 characters",
        },
      },
      frequency: isRecurring
        ? {
            required: "Frequency is required",
          }
        : {},
      interval: isRecurring
        ? {
            required: "Interval is required",
            min: { value: 1, message: "Interval must be greater than 0" },
          }
        : {},
      stopAfterOccurrences: isRecurring
        ? {
            min: { value: 1, message: "Occurrences must be greater than 0" },
          }
        : {},
      taxWithheld: REQUIRED_TAX_WITHHELD_RULES,
    }),
    [getValues, isRecurring, frequency, interval],
  );
  const maxTransactionDate = isRecurring ? undefined : localToday();

  const getTypeOptions = useCallback(() => {
    if (isRecurring) {
      return TYPE_OPTIONS.filter(
        (option) =>
          option.value !== TransactionTypeOptions.LOAN_PAYMENT &&
          option.value !== TransactionTypeOptions.MARKET_UPDATE,
      );
    }
    if (
      transaction?.transactionType.value === TransactionTypeOptions.LOAN_PAYMENT
    ) {
      return TYPE_OPTIONS.filter(
        (option) => option.value === TransactionTypeOptions.LOAN_PAYMENT,
      );
    } else if (transaction?.transactionType) {
      return TYPE_OPTIONS.filter(
        (option) => option.value !== TransactionTypeOptions.LOAN_PAYMENT,
      );
    }
    return TYPE_OPTIONS;
  }, [transaction?.transactionType, isRecurring]);

  const shouldShowTaxSwitch =
    transactionType.value === TransactionTypeOptions.INCOME;
  const isTaxAvailable = shouldShowTaxSwitch && !isRecurring;
  const shouldShowTax = isTaxAvailable && Boolean(hasTaxWithheld);
  const taxSwitchHelperText = isRecurring
    ? "Not available for recurring transactions"
    : "";

  const isRecurringDisabled = useMemo(() => {
    if (transaction?.id) {
      return true;
    }
    if (
      transactionType.value === TransactionTypeOptions.LOAN_PAYMENT ||
      transactionType.value === TransactionTypeOptions.MARKET_UPDATE
    ) {
      return true;
    }
    return shouldShowTax;
  }, [transaction?.id, transactionType.value, shouldShowTax]);

  const getRecurringHelperText = useMemo(() => {
    if (transaction?.id) {
      return "Cannot change recurring status when editing";
    }
    if (
      transactionType.value === TransactionTypeOptions.LOAN_PAYMENT ||
      transactionType.value === TransactionTypeOptions.MARKET_UPDATE
    ) {
      return `${transactionType.label} transactions cannot be recurring`;
    }
    if (shouldShowTax) {
      return "Not available with tax withheld";
    }
    return "";
  }, [
    transaction?.id,
    transactionType.value,
    transactionType.label,
    shouldShowTax,
  ]);

  useNetAmountWhileTaxHidden(
    shouldShowTax,
    effectiveTransaction,
    getValues,
    setValue,
  );

  const onSubmit: SubmitHandler<CreateTransactionsObject> = (data) =>
    handleOnSubmit(withTaxForSwitch(data, isTaxAvailable));

  const isEditAction = action === "edit";
  const headerTitle = category?.label ?? transactionType.label;

  const buildEditDetails = (
    values: CreateTransactionsObject,
    amount?: string,
  ): ConfirmDetail => {
    const typeValue = String(values.transactionType.value);
    return {
      pill: DISCARD_PILL_BY_TYPE[typeValue] ?? {
        label: values.transactionType.label.toUpperCase(),
        variant: "DEFAULT",
      },
      title: values.category?.label ?? values.transactionType.label,
      subtitle: joinDetailParts(
        values.subCategory?.label,
        formatDate(values.transactionDate, DateFormat.DMY_SPACE),
      ),
      amount,
      amountVariant: TONE_BY_TYPE[typeValue] ?? "neutral",
    };
  };

  const buildDiscardDetails = (): ConfirmDetail => {
    const values = getValues();
    const amountVariant =
      TONE_BY_TYPE[String(values.transactionType.value)] ?? "neutral";
    const amountNumber = Number.parseFloat(values.amount);
    const hasAmount = !Number.isNaN(amountNumber) && amountNumber > 0;
    const signedAmount =
      amountVariant === "expense" ? -amountNumber : amountNumber;
    const formattedAmount = hasAmount
      ? formatCurrency(signedAmount)
      : undefined;

    if (isEditAction) {
      return buildEditDetails(values, formattedAmount);
    }
    return buildDraftDetails({
      noun: values.transactionType.label.toLowerCase(),
      fieldValues: [
        values.amount,
        values.category,
        values.subCategory,
        values.fromAccount,
        values.toAccount,
        values.transactionDate,
        values.note,
      ],
      amount: formattedAmount,
      primaryLabel: values.category?.label,
    });
  };

  const fieldsProps: TransactionFieldsProps = {
    ...cashbackFields,
    action,
    transaction: effectiveTransaction,
    originalDate,
    control,
    register,
    setValue,
    isSubmitting,
    validationRules,
    typeOptions: getTypeOptions(),
    categoryOptions,
    subCategoryOptions,
    accountOptions,
    loanToAccountOptions: liabilityAccountOptions,
    frequencyOptions: FREQUENCY_OPTIONS,
    shouldShowPrincipalAmount:
      transactionType.value === TransactionTypeOptions.LOAN_PAYMENT,
    shouldShowTax,
    shouldShowTaxSwitch,
    isTaxSwitchDisabled: isRecurring,
    taxSwitchHelperText,
    shouldShowCategory:
      categoryOptions.length > 0 &&
      transactionType.value !== TransactionTypeOptions.TRANSFER,
    shouldShowSubCategory: subCategoryOptions.length > 0,
    shouldShowFromAccount:
      transactionType.value !== TransactionTypeOptions.INCOME,
    shouldShowToAccount:
      transactionType.value !== TransactionTypeOptions.EXPENSE,
    isRecurringDisabled,
    recurringHelperText: getRecurringHelperText,
    isRecurring,
    maxTransactionDate,
  };

  return (
    <FormPanel
      open={isOpen}
      onClose={onClose}
      ariaLabel="Transaction form"
      eyebrow={title}
      title={headerTitle}
      form={form}
      formId={FORM_ID}
      onSubmit={onSubmit}
      submitLabel={SUBMIT_LABELS[action]}
      isSubmitDisabled={cashbackFields.isCashbackPrefillPending}
      discard={{
        ...discardCopyOf(isEditAction, "transaction"),
        details: buildDiscardDetails,
      }}
      mobile={{
        title,
        submitLabel: MOBILE_SUBMIT_LABELS[action],
        fields: (
          <MobileTransactionForm
            {...fieldsProps}
            unregister={unregister}
            clearErrors={clearErrors}
          />
        ),
      }}
      load={load}
    >
      <DesktopTransactionForm {...fieldsProps} />
    </FormPanel>
  );
};
